import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { accountsAPI, brandingAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, 
  Loader2, 
  Upload,
  Palette,
  Image,
  Sparkles
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import { toast } from 'sonner';

export const BrandingPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  
  const [account, setAccount] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [accountRes, brandingRes] = await Promise.all([
        accountsAPI.get(accountId),
        brandingAPI.get(accountId)
      ]);
      setAccount(accountRes.data);
      setBranding(brandingRes.data);
    } catch (error) {
      console.error('Error loading branding data:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await brandingAPI.update(accountId, branding);
      toast.success('Branding updated successfully');
    } catch (error) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event, logoType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [logoType]: true }));
    try {
      const response = await brandingAPI.uploadLogo(accountId, file, logoType);
      setBranding(prev => ({
        ...prev,
        [`${logoType}_url`]: response.data.url
      }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(prev => ({ ...prev, [logoType]: false }));
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/accounts')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Custom Branding</h1>
          <p className="text-slate-400">{account?.name}</p>
        </div>
        <HelpButton 
          title="Custom Branding"
          content={`
            <p>Customize the appearance of your AI agents to match your brand.</p>
            <p class="mt-2"><strong>Logo Settings:</strong></p>
            <ul>
              <li><strong>Primary Logo:</strong> Displayed in the dashboard header</li>
              <li><strong>Dark Mode Logo:</strong> Used on dark backgrounds</li>
              <li><strong>Favicon:</strong> Browser tab icon</li>
              <li><strong>Widget Logo:</strong> Shown in the chat widget header</li>
            </ul>
            <p class="mt-2"><strong>Color Settings:</strong></p>
            <ul>
              <li><strong>Primary Color:</strong> Main accent color for buttons and highlights</li>
              <li><strong>Secondary Color:</strong> Used for gradients and secondary elements</li>
            </ul>
            <p class="mt-2"><strong>Tip:</strong> Use consistent colors that match your website for a seamless user experience.</p>
          `}
        />
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save Changes
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Logo Settings */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Image className="w-5 h-5" />
              Logo & Images
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <LogoUploader
              label="Primary Logo"
              description="Used in dashboard header"
              currentUrl={branding?.logo_url}
              logoType="logo"
              uploading={uploading.logo}
              onUpload={(e) => handleLogoUpload(e, 'logo')}
            />
            <LogoUploader
              label="Dark Mode Logo"
              description="Optional, for dark backgrounds"
              currentUrl={branding?.logo_dark_url}
              logoType="logo_dark"
              uploading={uploading.logo_dark}
              onUpload={(e) => handleLogoUpload(e, 'logo_dark')}
            />
            <LogoUploader
              label="Favicon"
              description="Browser tab icon"
              currentUrl={branding?.favicon_url}
              logoType="favicon"
              uploading={uploading.favicon}
              onUpload={(e) => handleLogoUpload(e, 'favicon')}
            />
            <LogoUploader
              label="Widget Logo"
              description="Shown in chat widget header"
              currentUrl={branding?.widget_logo_url}
              logoType="widget_logo"
              uploading={uploading.widget_logo}
              onUpload={(e) => handleLogoUpload(e, 'widget_logo')}
            />
          </CardContent>
        </Card>

        {/* Color Settings */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Brand Colors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ColorPicker
              label="Primary Color"
              value={branding?.primary_color || '#6366f1'}
              onChange={(color) => setBranding({ ...branding, primary_color: color })}
            />
            <ColorPicker
              label="Secondary Color"
              value={branding?.secondary_color || '#8b5cf6'}
              onChange={(color) => setBranding({ ...branding, secondary_color: color })}
            />
            <ColorPicker
              label="Accent Color"
              value={branding?.accent_color || '#f59e0b'}
              onChange={(color) => setBranding({ ...branding, accent_color: color })}
            />
            
            <div className="pt-4 border-t border-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Show &quot;Powered by Harmonix&quot;</Label>
                  <p className="text-sm text-slate-500">Display branding in widget footer</p>
                </div>
                <Switch
                  checked={branding?.powered_by_visible ?? true}
                  onCheckedChange={(checked) => setBranding({ ...branding, powered_by_visible: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-800 rounded-lg p-8">
            <div className="flex items-center gap-4 mb-6">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="h-10" />
              ) : (
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: branding?.primary_color || '#6366f1' }}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-white">{account?.name}</span>
            </div>
            
            <div className="flex gap-4">
              <div className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: branding?.primary_color || '#6366f1' }}>
                Primary Button
              </div>
              <div className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: branding?.secondary_color || '#8b5cf6' }}>
                Secondary Button
              </div>
              <div className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: branding?.accent_color || '#f59e0b' }}>
                Accent Button
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const LogoUploader = ({ label, description, currentUrl, logoType, uploading, onUpload }) => (
  <div className="flex items-center justify-between p-4 rounded-lg bg-slate-950/50 border border-slate-800">
    <div className="flex items-center gap-4">
      {currentUrl ? (
        <img src={currentUrl} alt={label} className="w-12 h-12 object-contain rounded" />
      ) : (
        <div className="w-12 h-12 bg-slate-800 rounded flex items-center justify-center">
          <Image className="w-6 h-6 text-slate-600" />
        </div>
      )}
      <div>
        <p className="font-medium text-white">{label}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </div>
    <label>
      <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 cursor-pointer" disabled={uploading} asChild>
        <span>
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          Upload
        </span>
      </Button>
      <input type="file" className="hidden" accept="image/*" onChange={onUpload} />
    </label>
  </div>
);

const ColorPicker = ({ label, value, onChange }) => (
  <div>
    <Label className="text-slate-400 mb-2 block">{label}</Label>
    <div className="flex gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded cursor-pointer border-0"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-950 border-slate-800 text-white flex-1"
      />
    </div>
  </div>
);

export default BrandingPage;
